<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import CreateContactListDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateContactListDialog.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const lists = useMapGetter('contactLists/getLists');
const uiFlags = useMapGetter('contactLists/getUIFlags');
const createDialogRef = ref(null);

const isFetching = computed(() => uiFlags.value.isFetching);
const hasLists = computed(() => lists.value.length > 0);

const listLabel = list =>
  list.folder_name ? `${list.folder_name} / ${list.name}` : list.name;

const openCreate = () => createDialogRef.value?.open('create');

const onCreate = async ({ name, description, folderName }) => {
  try {
    const list = await store.dispatch('contactLists/create', {
      list: { name, description, folder_name: folderName },
    });
    useAlert(t('CONTACTS_LAYOUT.LISTS.CREATE_SUCCESS'));
    if (list?.id) {
      router.push(
        accountScopedRoute(
          'contacts_dashboard_lists_index',
          { listId: list.id },
          { page: 1 }
        )
      );
    }
  } catch {
    useAlert(t('CONTACTS_LAYOUT.LISTS.ERROR'));
  }
};

onMounted(() => {
  store.dispatch('contactLists/get');
});
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div
        class="flex items-center justify-between w-full max-w-5xl py-6 mx-auto"
      >
        <span class="text-xl font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.LISTS.PAGE_TITLE') }}
        </span>
        <Button
          :label="t('CONTACTS_LAYOUT.LISTS.ADD')"
          icon="i-lucide-plus"
          size="sm"
          @click="openCreate"
        />
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div
        v-if="isFetching"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <EmptyStateLayout
        v-else-if="!hasLists"
        :title="t('CONTACTS_LAYOUT.LISTS.EMPTY_TITLE')"
        :subtitle="t('CONTACTS_LAYOUT.LISTS.EMPTY_SUBTITLE')"
        :show-backdrop="false"
      />
      <div v-else class="flex flex-col w-full max-w-5xl gap-2 pb-8 mx-auto">
        <router-link
          v-for="list in lists"
          :key="list.id"
          :to="
            accountScopedRoute(
              'contacts_dashboard_lists_index',
              { listId: list.id },
              { page: 1 }
            )
          "
          class="flex items-center justify-between px-4 py-3 rounded-xl bg-n-solid-1 hover:bg-n-alpha-2"
        >
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ listLabel(list) }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{
              t('CONTACTS_LAYOUT.LISTS.CONTACTS_COUNT', {
                count: list.contacts_count || 0,
              })
            }}
          </span>
        </router-link>
      </div>
    </main>
    <CreateContactListDialog ref="createDialogRef" @create="onCreate" />
  </section>
</template>
